import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { AppModuleLayout } from "@layouts";
import { PageHead, ClipLoaderCommon } from "@components";
import { cmsApi } from "@api";
import { CmsPostList } from "@interfaces";
import { theme, breakpoint, getSiteMeta, buildBreadcrumbJsonLd } from "@utilities";

type LoadState = "loading" | "ready" | "error";

const formatDate = (iso?: string | null) => {
    if (!iso) return "";
    const d = new Date(iso);
    return isNaN(d.getTime())
        ? ""
        : d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
};

/** Blog index — lists published posts with cover, excerpt and metadata. */
export const Blog = () => {
    const [searchParams] = useSearchParams();
    const tag = searchParams.get("tag") ?? undefined;
    const preview = searchParams.get("preview") === "1";
    const [state, setState] = useState<LoadState>("loading");
    const [list, setList] = useState<CmsPostList | null>(null);

    useEffect(() => {
        let active = true;
        setState("loading");
        window.scrollTo({ top: 0 });
        (async () => {
            try {
                const result = await cmsApi.listPosts({ tag, preview });
                if (!active) return;
                setList(result);
                setState("ready");
            } catch (err) {
                if (!active) return;
                console.error("[Blog] failed to load", err);
                setState("error");
            }
        })();
        return () => {
            active = false;
        };
    }, [tag, preview]);

    return (
        <AppModuleLayout>
            <PageHead
                seo={{
                    title: tag ? `Blog — ${tag}` : "Blog",
                    metaDescription: "Travel stories, guides and tips for your next adventure.",
                    structuredDataType: "WebPage",
                }}
                jsonLd={(() => {
                    const site = getSiteMeta();
                    return [
                        buildBreadcrumbJsonLd(
                            [
                                { name: "Home", url: site.siteUrl || "/" },
                                { name: "Blog", url: site.siteUrl ? `${site.siteUrl}/blog` : "/blog" },
                            ],
                            site,
                        ),
                    ];
                })()}
            />
            <Container>
                <header className="blogHeader">
                    <h1>Blog</h1>
                    {tag && <p className="tagLabel">Tagged “{tag}”</p>}
                </header>

                {state === "loading" && (
                    <ClipLoaderCommon color={theme.colors.highlight} size={60} margin={80} />
                )}
                {state === "error" && (
                    <div className="blogMessage">
                        <p>We couldn’t load the blog right now. Please try again shortly.</p>
                    </div>
                )}
                {state === "ready" && list && list.posts.length === 0 && (
                    <div className="blogMessage">
                        <p>No posts published yet. Check back soon.</p>
                    </div>
                )}
                {state === "ready" && list && list.posts.length > 0 && (
                    <div className="grid">
                        {list.posts.map((post) => (
                            <Link className="postCard" to={`/blog/${post.slug}`} key={post.id}>
                                {post.coverImage && (
                                    <img src={post.coverImage} alt={post.title} className="cover" />
                                )}
                                <div className="body">
                                    <h2>{post.title}</h2>
                                    {post.excerpt && <p className="excerpt">{post.excerpt}</p>}
                                    <div className="meta">
                                        {post.author?.name && <span>{post.author.name}</span>}
                                        {post.publishedAt && <span>{formatDate(post.publishedAt)}</span>}
                                        {post.readingMinutes && <span>{post.readingMinutes} min read</span>}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </Container>
        </AppModuleLayout>
    );
};

const Container = styled.div`
    background-color: #fff;
    width: 100%;
    max-width: 1110px;
    margin: 0 auto;
    min-height: 60vh;
    padding: 32px 16px 60px;

    .blogHeader h1 {
        font-size: 34px;
        font-weight: 700;
        margin: 0;
    }
    .tagLabel {
        color: ${theme.colors.gray_2};
        margin-top: 4px;
    }
    .blogMessage {
        text-align: center;
        padding: 60px 16px;
        color: ${theme.colors.gray_2};
    }
    .grid {
        margin-top: 24px;
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        gap: 20px;
    }
    .postCard {
        display: block;
        border: 1px solid #eee;
        border-radius: 12px;
        overflow: hidden;
        text-decoration: none;
        color: inherit;
        background: #fff;
        transition: box-shadow 0.2s ease;
    }
    .postCard:hover {
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
    }
    .cover {
        width: 100%;
        height: 180px;
        object-fit: cover;
        display: block;
    }
    .body {
        padding: 16px;
    }
    .body h2 {
        font-size: 20px;
        font-weight: 700;
        margin: 0 0 8px;
    }
    .excerpt {
        color: ${theme.colors.gray_2};
        font-size: 14px;
        line-height: 1.5;
    }
    .meta {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        margin-top: 12px;
        font-size: 12px;
        color: ${theme.colors.gray_2};
    }
    ${breakpoint.breakMobile`
        .grid { grid-template-columns: 1fr; }
    `}
`;
